import { useState, useEffect } from "react";
import { Icons } from "../../../icons";
import TypeAhead from "./Typeahead";
import SelectChip from "./SelectChip";
import { cn } from "../../../utils/css";
import { SelectOption } from "../constants/signup.types";

interface SingleSelectProps {
  options: SelectOption[];
  onChange: (value: any) => void;
  progressOnSelect: () => void;
  value?: string;
  extraOptions?: SelectOption[];
  typeaheadPlaceholder?: string;
}

const SingleSelect = ({
  options,
  onChange,
  progressOnSelect,
  value,
  extraOptions,
  typeaheadPlaceholder,
}: SingleSelectProps) => {
  const PROGRESS_DELAY_MS = 500;
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(
    () => options.find((opt) => opt.value === value)?.id,
  );
  const [showTypeahead, setShowTypeahead] = useState(false);

  useEffect(() => {
    const currentOptionId = options.find((opt) => opt.value === value)?.id;
    if (currentOptionId !== selectedOptionId) {
      setSelectedOptionId(currentOptionId);
    }
  }, [value, options, selectedOptionId]);

  const handleSelect = (option: SelectOption) => {
    if (option?.enableSearchDropdown) {
      setShowTypeahead(true);
      return;
    }

    setSelectedOptionId(option.id);
    onChange(option.value);
    setShowTypeahead(false);

    setTimeout(() => {
      progressOnSelect();
    }, PROGRESS_DELAY_MS);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {showTypeahead ? (
        <div className="tw-flex tw-flex-col tw-gap-4">
          <button
            onClick={() => setShowTypeahead(false)}
            className={cn(
              "tw-inline-flex tw-items-center tw-gap-1 tw-text-gray-500 tw-p-2 tw-w-fit",
              "hover:tw-text-gray-700 hover:tw-underline",
              "focus:tw-bg-gray-50",
            )}
          >
            <Icons.ArrowLeft className="tw-w-4 tw-h-4" />
            <p>Back</p>
          </button>
          <TypeAhead
            onChange={onChange}
            options={extraOptions}
            placeHolder={typeaheadPlaceholder}
            progressOnSelect={progressOnSelect}
          />
        </div>
      ) : (
        <div className="tw-relative">
          <div
            role="radiogroup"
            aria-label="Select option"
            className={cn(
              "tw-flex tw-flex-col tw-gap-5 tw-w-full tw-items-center",
            )}
          >
            {options.map((option) => {
              const isCurrentlySelected = selectedOptionId === option.id;
              return (
                <SelectChip
                  key={option.id}
                  option={option}
                  isSelected={isCurrentlySelected}
                  onSelect={handleSelect}
                  ariaRole="radio"
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleSelect;
